import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from '../components/ui/Logo';
import { useAuth } from '../context/AuthContext';
import { FiTarget } from 'react-icons/fi';
import ThreeIcon from '../components/ui/ThreeIcon';
import { WelcomeModal, startLandingTour } from '../components/onboarding';
import { apiClient, socketClient } from '../services/api';
import { useTranslation } from 'react-i18next';
import { LanguageCurrencyPicker } from '../components/layout/LanguageCurrencyPicker';

const LandingPage: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { user, isAuthenticated, logout } = useAuth();
    const [showNotifications, setShowNotifications] = React.useState(false);
    const [showSettings, setShowSettings] = React.useState(false);
    const [notifications, setNotifications] = React.useState<any[]>([]);
    const [unreadCount, setUnreadCount] = React.useState(0);
    const [isLocked, setIsLocked] = React.useState(false);
    const [is3FAEnabled, setIs3FAEnabled] = React.useState(false);

    const handleLockSite = () => {
        setIsLocked(true);
        setShowSettings(false);
    };

    const handleUnlock = (e: React.FormEvent) => {
        e.preventDefault();
        setIsLocked(false);
    };

    const toggle3FA = () => setIs3FAEnabled(!is3FAEnabled);

    // Fetch notifications from backend
    React.useEffect(() => {
        if (!isAuthenticated) return;

        const fetchNotifications = async () => {
            try {
                const response = await apiClient.getUserNotifications();
                if (response && response.notifications) {
                    setNotifications(response.notifications);
                    setUnreadCount(response.unreadCount || 0);
                }
            } catch (error) {
                console.error('Failed to fetch notifications on landing:', error);
            }
        };

        fetchNotifications();
        const intervalId = setInterval(fetchNotifications, 30000);

        // Socket listener for real-time notification refresh on landing
        const unsubscribe = socketClient.subscribe('task_match', () => {
            fetchNotifications();
        });

        return () => {
            clearInterval(intervalId);
            unsubscribe();
        };
    }, [isAuthenticated]);

    const handleRegister = () => {
        navigate('/register');
    };

    const handleExplore = () => {
        navigate('/projects');
    };

    // Auto-resume tour if returning from deep dive
    React.useEffect(() => {
        const hash = window.location.hash;
        const queryStart = hash.indexOf('?');
        const queryString = queryStart >= 0 ? hash.substring(queryStart + 1) : '';
        const searchParams = new URLSearchParams(queryString);

        if (searchParams.get('autoStartTour') === 'true') {
            // Slight delay to ensure DOM is ready and driver.js can attach
            setTimeout(() => {
                startLandingTour();
            }, 800);
        }
    }, []);
    if (isLocked) {
        return (
            <div className="fixed inset-0 z-[100] bg-premium-wood-dark/95 backdrop-blur-xl flex flex-col items-center justify-center p-4">
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="bg-black/40 p-8 rounded-2xl border border-premium-gold/30 flex flex-col items-center max-w-sm w-full"
                >
                    <div className="mb-6"><Logo size="lg" /></div>
                    <h2 className="text-2xl text-premium-gold font-serif mb-2">Site Locked</h2>
                    <p className="text-white/60 text-sm mb-6 text-center">Please enter your password to resume.</p>

                    <form onSubmit={handleUnlock} className="w-full">
                        <input
                            type="password"
                            placeholder="Enter Password"
                            className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white mb-4 focus:border-premium-gold outline-none"
                            autoFocus
                        />
                        <button
                            type="submit"
                            className="w-full bg-premium-gold text-premium-wood-dark font-bold py-3 rounded-lg hover:bg-premium-gold-light transition-colors"
                        >
                            Unlock
                        </button>
                    </form>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-premium-wood-dark font-sans text-white overflow-x-hidden relative">
            {/* ... backgrounds ... */}
            <div
                className="absolute inset-0 z-0 opacity-40 pointer-events-none mix-blend-overlay"
                style={{
                    backgroundImage: `url('https://images.unsplash.com/photo-1543158143-4a1c0d0c3405?q=80&w=1600&auto=format&fit=crop&q=60')`,
                    backgroundSize: 'cover',
                    filter: 'contrast(1.1) brightness(0.7)'
                }}
            />

            <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/60 via-premium-wood-dark/80 to-black/90 pointer-events-none" />

            {/* Header */}
            <header className="relative z-50 flex items-center justify-between px-3 sm:px-6 py-4 sm:py-6 md:px-12 max-w-7xl mx-auto border-b border-premium-gold/20">
                <div className="flex items-center gap-1.5 sm:gap-3 cursor-pointer" onClick={() => navigate('/')}>
                    <Logo variant="sidebar" size="sm" />
                </div>

                <div className="flex items-center gap-1.5 sm:gap-3 md:gap-4">
                    <LanguageCurrencyPicker />

                    {/* Dispatch/Jobs Icon */}
                    <button
                        id="landing-header-dispatch"
                        onClick={() => navigate('/dispatch/applications')}
                        className="p-2 rounded-full text-white/70 hover:text-white transition-all duration-300 relative hover:bg-white/5"
                        title="Job Board"
                    >
                        <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                    </button>

                    {/* Notification Bell */}
                    <div className="relative">
                        <button
                            onClick={() => setShowNotifications(!showNotifications)}
                            className={`p-2 rounded-full transition-all duration-300 relative ${showNotifications ? 'text-premium-gold' : 'text-white/70 hover:text-white'}`}
                        >
                            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                            </svg>
                            {unreadCount > 0 && (
                                <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border border-[#1a1a1a]"></span>
                            )}
                        </button>

                        <AnimatePresence>
                            {showNotifications && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                    className="absolute right-0 mt-4 w-80 bg-[#1a1a1a] border border-premium-gold/20 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.5)] overflow-hidden z-[60]"
                                >
                                    <div className="p-4 border-b border-white/5 flex justify-between items-center bg-premium-wood-dark/50">
                                        <h3 className="text-white font-bold">Notifications</h3>
                                        <span className="text-xs text-premium-gold cursor-pointer hover:underline" onClick={() => navigate('/notifications')}>View all</span>
                                    </div>
                                    <div className="max-h-96 overflow-y-auto">
                                        {notifications.length === 0 ? (
                                            <div className="p-8 text-center text-white/40 text-sm">
                                                No new notifications
                                            </div>
                                        ) : (
                                            notifications.map(notif => (
                                                <div
                                                    key={notif._id || notif.id}
                                                    onClick={() => {
                                                        if (!notif.isRead && !notif.read) {
                                                            apiClient.markNotificationAsRead(notif._id || notif.id || '');
                                                            setNotifications(prev => prev.map(n =>
                                                                (n._id === notif._id || n.id === notif.id) ? { ...n, isRead: true, read: true } : n
                                                            ));
                                                        }
                                                        navigate('/notifications');
                                                        setShowNotifications(false);
                                                    }}
                                                    className={`p-4 border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer ${(!notif.isRead && !notif.read) ? 'bg-white/5' : ''}`}
                                                >
                                                    <div className="flex gap-3">
                                                        <div className="text-2xl mt-1">
                                                            {notif.type === 'talent_qualification' ? '🏆' :
                                                                notif.type === 'profile_incomplete' ? '📝' :
                                                                    notif.type === 'application' ? '💼' :
                                                                        notif.type === 'comment' ? '💬' :
                                                                            '📢'}
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className="flex justify-between items-start mb-1">
                                                                <h4 className="font-bold text-white text-sm">{notif.title}</h4>
                                                                <span className="text-[10px] text-white/30">
                                                                    {notif.createdAt ? new Date(notif.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Recently'}
                                                                </span>
                                                            </div>
                                                            <p className="text-xs text-white/70 mb-3">{notif.message}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {isAuthenticated ? (
                        <div className="relative">
                            <button
                                onClick={() => setShowSettings(!showSettings)}
                                className="flex items-center gap-2 sm:gap-3 px-2 py-1 sm:px-4 sm:py-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 hover:border-premium-gold/30 transition-all group"
                            >
                                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-premium-gold to-premium-gold-dark text-premium-wood-dark font-bold flex items-center justify-center text-sm shadow-[0_0_15px_rgba(212,175,55,0.3)] overflow-hidden">
                                    {user?.avatar ? (
                                        <img src={user.avatar} alt="Profile" className="w-full h-full object-cover" />
                                    ) : (
                                        <span>{user?.firstName?.[0]}{user?.lastName?.[0]}</span>
                                    )}
                                </div>
                                <div className="hidden sm:flex flex-col items-start">
                                    <span className="text-white text-xs sm:text-sm font-medium leading-none">{user?.firstName} {user?.lastName}</span>
                                    <span className="text-premium-gold text-[9px] sm:text-[10px] uppercase tracking-wider mt-0.5">{user?.role || 'Member'}</span>
                                </div>
                                <svg className={`w-4 h-4 text-white/50 transition-transform duration-300 ${showSettings ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            <AnimatePresence>
                                {showSettings && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                        className="absolute right-0 mt-4 w-64 sm:w-72 bg-[#1a1a1a] border border-premium-gold/20 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.5)] overflow-hidden z-[60]"
                                    >
                                        <div className="p-4 border-b border-white/5 bg-premium-wood-dark/50">
                                            <div className="flex items-center gap-3 mb-2">
                                                <div className="w-10 h-10 rounded-full bg-premium-gold flex items-center justify-center text-premium-wood-dark font-bold overflow-hidden shadow-inner">
                                                    {user?.avatar ? (
                                                        <img src={user.avatar} alt="Profile" className="w-full h-full object-cover" />
                                                    ) : (
                                                        <span>{user?.firstName?.[0]}{user?.lastName?.[0]}</span>
                                                    )}
                                                </div>
                                                <div className="overflow-hidden">
                                                    <div className="text-white font-bold truncate">{user?.username || 'Member'}</div>
                                                    <div className="text-[10px] text-white/50 truncate">{user?.email}</div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="p-2 space-y-1">
                                            <button onClick={() => { navigate('/dashboard'); setShowSettings(false); }} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-white hover:bg-white/5 hover:text-premium-gold transition-colors text-left group">
                                                <span className="p-2 rounded-md bg-white/5 group-hover:bg-premium-gold/20 transition-colors">📊</span>
                                                <div className="flex-1">
                                                    <div className="text-sm font-medium">Dashboard</div>
                                                    <div className="text-[10px] text-white/40">Access your workspace</div>
                                                </div>
                                            </button>

                                            <button onClick={() => { navigate('/settings'); setShowSettings(false); }} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-white hover:bg-white/5 hover:text-premium-gold transition-colors text-left group">
                                                <span className="p-2 rounded-md bg-white/5 group-hover:bg-premium-gold/20 transition-colors">⚙️</span>
                                                <div className="flex-1">
                                                    <div className="text-sm font-medium">Settings</div>
                                                    <div className="text-[10px] text-white/40">Edit profile & preferences</div>
                                                </div>
                                            </button>

                                            <button onClick={() => { navigate('/talent'); setShowSettings(false); }} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-white hover:bg-white/5 hover:text-premium-gold transition-colors text-left group">
                                                <span className="p-2 rounded-md bg-white/5 group-hover:bg-premium-gold/20 transition-colors">👤</span>
                                                <div className="flex-1">
                                                    <div className="text-sm font-medium">My Public Profile</div>
                                                    <div className="text-[10px] text-white/40">View as others see you</div>
                                                </div>
                                            </button>

                                            <button onClick={() => { navigate('/financial'); setShowSettings(false); }} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-white hover:bg-white/5 hover:text-premium-gold transition-colors text-left group">
                                                <span className="p-2 rounded-md bg-white/5 group-hover:bg-premium-gold/20 transition-colors">💰</span>
                                                <div className="flex-1">
                                                    <div className="text-sm font-medium">My Vault</div>
                                                    <div className="text-[10px] text-white/40">Manage your earnings & assets</div>
                                                </div>
                                            </button>

                                            <button onClick={handleLockSite} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-white hover:bg-white/5 hover:text-premium-gold transition-colors text-left group">
                                                <span className="p-2 rounded-md bg-white/5 group-hover:bg-premium-gold/20 transition-colors">🔒</span>
                                                <div className="flex-1">
                                                    <div className="text-sm font-medium">Lock Site</div>
                                                    <div className="text-[10px] text-white/40">Require password to resume</div>
                                                </div>
                                            </button>

                                            <button onClick={toggle3FA} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-white hover:bg-white/5 hover:text-premium-gold transition-colors text-left group">
                                                <span className="p-2 rounded-md bg-white/5 group-hover:bg-premium-gold/20 transition-colors">🛡️</span>
                                                <div className="flex-1">
                                                    <div className="text-sm font-medium flex justify-between items-center">
                                                        Enable 3FA
                                                        <div className={`w-8 h-4 rounded-full relative transition-colors ${is3FAEnabled ? 'bg-premium-gold' : 'bg-white/20'}`}>
                                                            <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${is3FAEnabled ? 'left-4.5' : 'left-0.5'}`} style={{ left: is3FAEnabled ? '18px' : '2px' }} />
                                                        </div>
                                                    </div>
                                                    <div className="text-[10px] text-white/40">Three-Factor Authentication</div>
                                                </div>
                                            </button>

                                            <button className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-white hover:bg-white/5 hover:text-premium-gold transition-colors text-left group">
                                                <span className="p-2 rounded-md bg-white/5 group-hover:bg-premium-gold/20 transition-colors">🔑</span>
                                                <div className="flex-1">
                                                    <div className="text-sm font-medium">Change Password</div>
                                                </div>
                                            </button>

                                            <button onClick={() => { startLandingTour(); setShowSettings(false); }} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-white hover:bg-white/5 hover:text-premium-gold transition-colors text-left group">
                                                <span className="p-2 rounded-md bg-white/5 group-hover:bg-premium-gold/20 transition-colors">🎯</span>
                                                <div className="flex-1">
                                                    <div className="text-sm font-medium">Landing Tour</div>
                                                    <div className="text-[10px] text-white/40">Learn about this page's features</div>
                                                </div>
                                            </button>
                                        </div>

                                        <div className="p-2 border-t border-white/5 bg-red-500/5">
                                            <button onClick={() => { logout(); setShowSettings(false); }} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors text-left">
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path></svg>
                                                <span className="text-sm font-medium">Sign Out</span>
                                            </button>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ) : (
                        <>
                            <button
                                id="landing-login-btn"
                                onClick={() => navigate('/login')}
                                className="px-2 sm:px-6 py-2 sm:py-2.5 text-white/90 hover:text-premium-gold transition-all border border-transparent hover:border-premium-gold/30 rounded-lg font-medium text-sm sm:text-base whitespace-nowrap"
                            >
                                {t('landing.login')}
                            </button>
                            <button
                                id="landing-signup-btn"
                                onClick={handleRegister}
                                className="px-2.5 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-premium-gold-dark to-premium-gold-light text-premium-wood-dark font-bold rounded-lg hover:shadow-[0_0_20px_rgba(212,175,55,0.5)] transition-all transform hover:scale-105 active:scale-95 text-sm sm:text-base whitespace-nowrap"
                            >
                                {t('landing.signup')}
                            </button>
                        </>
                    )}
                </div>
            </header>

            {/* Hero Section */}
            <main className="relative z-10 container mx-auto px-4 pt-16 pb-24 text-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="max-w-4xl mx-auto"
                >
                    <div className="flex items-center justify-center gap-2 sm:gap-4 mb-2">
                        <div className="h-[1px] w-8 sm:w-16 bg-gradient-to-r from-transparent to-premium-gold/50"></div>
                        <span className="font-serif italic text-premium-gold-light text-base sm:text-lg">{t('landing.welcome_to')}</span>
                        <div className="h-[1px] w-8 sm:w-16 bg-gradient-to-l from-transparent to-premium-gold/50"></div>
                    </div>

                    <div className="mb-8">
                        <Logo variant="header" />
                    </div>

                    <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-12 font-light leading-relaxed">
                        {t('landing.hero_desc')}
                    </p>

                    <div id="landing-hero-actions" className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-20 relative">
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="px-8 py-3 bg-gradient-to-b from-premium-gold-light to-premium-gold text-premium-wood-dark font-bold rounded-lg shadow-lg hover:shadow-[0_0_20px_rgba(212,175,55,0.3)] transition-all duration-300 w-full sm:w-auto"
                        >
                            {t('landing.go_to_dashboard')}
                        </button>
                        <button
                            onClick={() => navigate('/dispatch')}
                            className="px-8 py-3 border border-premium-gold/30 bg-black/40 backdrop-blur-sm rounded-lg text-premium-gold hover:border-premium-gold/60 hover:bg-premium-gold/5 transition-all duration-300 w-full sm:w-auto flex items-center justify-center gap-2"
                        >
                            <FiTarget className="text-xl" /> {t('landing.go_to_dispatch')}
                        </button>
                        <button
                            onClick={handleExplore}
                            className="px-8 py-3 border border-premium-wood-light bg-black/40 backdrop-blur-sm rounded-lg text-white hover:border-premium-gold hover:text-premium-gold transition-all duration-300 w-full sm:w-auto"
                        >
                            {t('landing.explore_projects')}
                        </button>
                    </div>


                    {/* Large Prominent Team Image */}
                    <div className="mb-16 relative">
                        <div className="max-w-2xl mx-auto">
                            <div className="relative rounded-2xl overflow-hidden border-4 border-premium-gold shadow-[0_0_40px_rgba(212,175,55,0.3)]">
                                <img
                                    src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=2670&auto=format&fit=crop"
                                    alt="Team collaboration"
                                    className="w-full h-auto object-cover opacity-90"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                            </div>
                        </div>
                    </div>

                    {/* Navigation Grid */}
                    <div className="space-y-8">
                        <h2 className="text-3xl md:text-4xl font-serif font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-premium-gold-light to-premium-gold mb-12">
                            {t('landing.explore_our_platform')}
                        </h2>

                        <div id="landing-nav-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
                            <NavigationCard
                                id="nav-card-dashboard"
                                title={t('landing.nav_dashboard_title')}
                                description={t('landing.nav_dashboard_desc')}
                                icon={<ThreeIcon type="dashboard" />}
                                onClick={() => navigate('/dashboard')}
                            />
                            <NavigationCard
                                id="nav-card-projects"
                                title={t('landing.nav_projects_title')}
                                description={t('landing.nav_projects_desc')}
                                icon={<ThreeIcon type="projects" />}
                                onClick={() => navigate('/projects')}
                            />
                            <NavigationCard
                                id="nav-card-dispatch"
                                title={t('landing.nav_dispatch_title')}
                                description={t('landing.nav_dispatch_desc')}
                                icon={<ThreeIcon type="dispatch" />}
                                onClick={() => navigate('/dispatch')}
                            />
                            <NavigationCard
                                id="nav-card-skills"
                                title={t('landing.nav_skills_title')}
                                description={t('landing.nav_skills_desc')}
                                icon={<ThreeIcon type="skills" />}
                                onClick={() => navigate('/skills')}
                            />
                            <NavigationCard
                                id="nav-card-workspace"
                                title={t('landing.nav_workspace_title')}
                                description={t('landing.nav_workspace_desc')}
                                icon={<ThreeIcon type="workspace" />}
                                onClick={() => navigate('/workspace')}
                            />
                            <NavigationCard
                                id="nav-card-academy"
                                title={t('landing.nav_academy_title')}
                                description={t('landing.nav_academy_desc')}
                                icon={<ThreeIcon type="academy" />}
                                onClick={() => navigate('/academy')}
                            />
                            <NavigationCard
                                id="nav-card-talent"
                                title={t('landing.nav_talent_title')}
                                description={t('landing.nav_talent_desc')}
                                icon={<ThreeIcon type="talent" />}
                                onClick={() => navigate('/talent')}
                            />
                            <NavigationCard
                                id="nav-card-client"
                                title={t('landing.nav_client_title')}
                                description={t('landing.nav_client_desc')}
                                icon={<ThreeIcon type="client" />}
                                onClick={() => navigate('/client')}
                            />
                            <NavigationCard
                                id="nav-card-3dhub"
                                title={t('landing.nav_3dhub_title')}
                                description={t('landing.nav_3dhub_desc')}
                                icon={<ThreeIcon type="3dhub" />}
                                onClick={() => navigate('/3dhub')}
                            />
                            <NavigationCard
                                id="nav-card-aihub"
                                title={t('landing.nav_aihub_title')}
                                description={t('landing.nav_aihub_desc')}
                                icon={<ThreeIcon type="aihub" />}
                                onClick={() => navigate('/unitybot')}
                            />
                            <NavigationCard
                                id="nav-card-financial"
                                title={t('landing.nav_vault_title')}
                                description={t('landing.nav_vault_desc')}
                                icon={<ThreeIcon type="financial" />}
                                onClick={() => navigate('/financial')}
                            />
                        </div>
                    </div>
                </motion.div>
            </main>

            {/* Footer */}
            <footer className="relative z-10 border-t border-white/10 bg-black/40 backdrop-blur-md pt-16 pb-24">
                <div className="container mx-auto px-4">
                    <div className="grid md:grid-cols-4 gap-12 mb-12">
                        <div className="col-span-1 md:col-span-2">
                            <Logo variant="sidebar" size="md" />
                            <p className="mt-6 text-white/60 max-w-sm leading-relaxed">
                                {t('landing.footer_desc')}
                            </p>
                        </div>
                        <div>
                            <h4 className="text-premium-gold font-bold mb-6">{t('landing.company')}</h4>
                            <ul className="space-y-4 text-white/70">
                                <li><a href="/#/about" className="hover:text-white transition-colors">{t('landing.about_us')}</a></li>
                                <li><a href="/#/contact" className="hover:text-white transition-colors">{t('landing.contact_us')}</a></li>
                                <li><a href="mailto:support@techunityglobal.com" className="hover:text-white transition-colors">support@techunityglobal.com</a></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-premium-gold font-bold mb-6">{t('landing.legal')}</h4>
                            <ul className="space-y-4 text-white/70">
                                <li><a href="/#/privacy" className="hover:text-white transition-colors">{t('landing.privacy_policy')}</a></li>
                                <li><a href="/#/terms" className="hover:text-white transition-colors">{t('landing.terms_of_service')}</a></li>
                            </ul>
                        </div>
                    </div>
                    <div className="pt-8 border-t border-white/10 text-center text-white/40 text-sm">
                        &copy; {new Date().getFullYear()} TechUnity Global. All rights reserved.
                    </div>
                </div>
            </footer>

            {/* Footer Decoration */}
            <div className="fixed bottom-0 left-0 right-0 z-50 flex items-end justify-between pointer-events-none">
                <div className="h-2 w-full bg-gradient-to-r from-premium-wood-dark via-premium-gold to-premium-wood-dark absolute bottom-0"></div>

                {/* Manual Tour Trigger */}
                <button
                    onClick={() => {
                        window.dispatchEvent(new Event('techunity-retake-tour'));
                    }}
                    className="pointer-events-auto relative z-50 m-4 px-3 py-1.5 bg-black/50 backdrop-blur-md border border-premium-gold/30 rounded-lg text-premium-gold/70 hover:text-premium-gold hover:border-premium-gold/60 text-xs font-medium transition-all opacity-50 hover:opacity-100 flex items-center gap-2"
                >
                    <span className="text-lg">↺</span> Retake Tour
                </button>
            </div>

            {/* Welcome Modal & Tour */}
            <WelcomeModal onStartTour={startLandingTour} />
        </div>
    );
};

interface NavigationCardProps {
    id?: string;
    title: string;
    description: string;
    icon: React.ReactNode;
    onClick: () => void;
}

const NavigationCard: React.FC<NavigationCardProps> = ({ id, title, description, icon, onClick }) => {
    return (
        <div
            id={id}
            onClick={onClick}
            className="group relative p-6 border border-white/10 rounded-xl bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm overflow-hidden hover:border-premium-gold/50 transition-all duration-500 hover:-translate-y-2 cursor-pointer"
        >
            <div className="absolute inset-0 bg-gradient-to-br from-premium-gold/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

            <div className="relative z-10 flex flex-col items-center text-center">
                <div className="w-32 h-32 mb-4 flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                    {icon}
                </div>
                <h3 className="font-serif text-xl font-bold text-premium-gold mb-2">{title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                    {description}
                </p>
            </div>

            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-white/20 group-hover:border-premium-gold transition-colors"></div>
            <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-white/20 group-hover:border-premium-gold transition-colors"></div>
            <div className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-white/20 group-hover:border-premium-gold transition-colors"></div>
            <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-white/20 group-hover:border-premium-gold transition-colors"></div>
        </div>
    );
};

export default LandingPage;
